import type { FC } from 'react';
import React, { useCallback, Fragment } from 'react';
import type { QueryResult } from 'react-apollo';
import { defineMessages, useIntl } from 'react-intl-next';
import type { ApolloQueryResult } from 'apollo-client';
import { NetworkStatus } from 'apollo-client';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { N0, N40 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { Inline, Text } from '@atlaskit/primitives';
import AkSpinner from '@atlaskit/spinner/spinner';
import Button from '@atlaskit/button/custom-theme-button';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';

import { fg } from '@confluence/feature-gating';

import { FeedType } from '../useFeedTypeSelector';
import { FeedOnboardingMessage } from '../Changeboarding';
import { FeedRecommendations, useFeedRecommendations } from '../FeedRecommendations';
import { FETCH_MORE_PAGE_SIZE } from '../feedConstants';
import type { FeedContentQuery as FeedContentQueryType } from '../__types__/FeedContentQuery';
import type { FollowingFeedSortByValue } from '../Feed';

import { useFeedShouldHideComments } from './useFeedShouldHideComments';
import { FeedCard } from './FeedCard';
import { FeedCardsWrapper } from './FeedCardsWrapper';
import { ExitFeedAllyButton } from './ExitFeedAllyButton';

type Props = {
	feedContentQuery: QueryResult<FeedContentQueryType>;
	openActivityFilterModal: () => void;
	handleFocusRef: () => void;
	followingFeedSortBy?: FollowingFeedSortByValue;
};

export const i18n = defineMessages({
	allActivity: {
		id: 'feed.fallback.message',
		defaultMessage: 'To see personalized activity edit your feed by following people and spaces.',
		description: 'Tells the user that we are falling back to all activity on empty feed',
	},
	manageFeed: {
		id: 'feed.fallback.manage.following',
		defaultMessage: 'Edit feed',
		description: 'Button text that opens a modal that allows the user to configure settings',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const FeedRecommendationsContainer = styled.div`
	background: ${token('color.background.input', N0)};
	position: relative;
	padding: ${token('space.100', '8px')} ${token('space.200', '16px')};
	padding-bottom: ${token('space.200', '16px')};
	margin-bottom: ${token('space.200', '16px')};
	overflow: hidden;
	height: 194px;
	border: 1px solid ${token('color.border', N40)};
	border-radius: ${token('border.radius.200', '8px')};
	&:hover {
		box-shadow: ${token(
			'elevation.shadow.raised',
			'0px 1px 1px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
		)};
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FallbackToAllActivityBannerWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	textAlign: 'left',
	background:
		'linear-gradient(208.78deg,rgba(255, 240, 179, 0.41) 1.19%,rgba(179, 245, 255, 0.41) 98.53%)', // eslint-disable-line @atlaskit/design-system/ensure-design-token-usage
	padding: token('space.150', '12px'),
	borderRadius: token('border.radius.200', '8px'),
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const SpinnerWrapper = styled.div`
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export const ActivityFeedCards: FC<Props> = ({
	feedContentQuery,
	openActivityFilterModal,
	handleFocusRef,
	followingFeedSortBy,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error, networkStatus, fetchMore } = feedContentQuery;
	const hasNextPage = data?.feed?.pageInfo?.hasNextPage;
	const endCursor = data?.feed?.pageInfo?.endCursor;
	const results = data?.feed?.nodes || [];
	const servingRecommendations = !!data?.getFeedUserConfig?.servingRecommendations;

	const { showFeedRecs, setShowFeedRecs, recsLoading, recs, setRecs, feedRecError } =
		useFeedRecommendations();

	const shouldHideComments = useFeedShouldHideComments();

	const linkButtonTheme = (currentTheme, themeProps) => {
		const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
		return {
			buttonStyles: {
				...buttonStyles,
				fontWeight: 400,
			},
			spinnerStyles,
		};
	};

	const injectFeedSortByIntoCursor = (cursor: string, feedSortBy?: FollowingFeedSortByValue) => {
		if (!feedSortBy) {
			return cursor;
		}

		const decodedCursor = atob(cursor);
		const parsedCursor = JSON.parse(decodedCursor);

		if (parsedCursor.feedSortOrder === feedSortBy.toUpperCase()) {
			return cursor;
		}

		return btoa(JSON.stringify({ ...parsedCursor, feedSortOrder: feedSortBy.toUpperCase() }));
	};

	// TODO: Remove this once we have fulled rolled out FG and CCI-2297 is fully shipped in cc-smarts
	const removeInjectedFeedSortByFromCursor = (cursor: string) => {
		if (fg('confluence_frontend_feed_sort_order_in_cursor')) {
			return cursor;
		}

		const decodedCursor = atob(cursor);
		const parsedCursor = JSON.parse(decodedCursor);
		return btoa(JSON.stringify({ feedCardIndex: parsedCursor.feedCardIndex }));
	};

	const loadNextPage = useCallback(async (): Promise<
		ApolloQueryResult<FeedContentQueryType> | undefined
	> => {
		try {
			if (!endCursor) {
				return undefined;
			}

			const newResult = await fetchMore({
				variables: {
					first: FETCH_MORE_PAGE_SIZE,
					after: removeInjectedFeedSortByFromCursor(endCursor),
					sortBy: followingFeedSortBy,
				},
				updateQuery: (prevResults: FeedContentQueryType, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResults;
					}
					fetchMoreResult.feed.nodes = prevResults.feed.nodes.concat(fetchMoreResult.feed.nodes);

					if (fetchMoreResult.feed.pageInfo.endCursor) {
						fetchMoreResult.feed.pageInfo.endCursor = injectFeedSortByIntoCursor(
							fetchMoreResult.feed.pageInfo.endCursor,
							followingFeedSortBy,
						);
					}

					return fetchMoreResult;
				},
			});
			if (newResult.data) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'paginated',
						actionSubject: 'feed',
						source: 'homeScreen',
						attributes: {
							resultCount: data ? newResult.data.feed.nodes.length - data?.feed.nodes.length : -1,
						},
					},
				}).fire();
			}

			if (newResult.errors) {
				throw newResult.errors;
			}

			return newResult;
		} catch (e) {
			throw e;
		}
	}, [endCursor, fetchMore, followingFeedSortBy, createAnalyticsEvent, data]);

	const disableAnimation = Boolean(
		process.env.REACT_SSR || (window.__SSR_RENDERED__ && networkStatus !== NetworkStatus.fetchMore),
	);

	const handleManageButtonClick = useCallback(() => {
		openActivityFilterModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'item',
				actionSubjectId: 'editFeed',
				source: 'homeScreen',
			},
		}).fire();
	}, [createAnalyticsEvent, openActivityFilterModal]);

	const shouldFallbackBasedOnCursor = (cursor) => {
		if (!cursor) return false;
		try {
			const decodedCursor = atob(cursor);
			const parsedCursor = JSON.parse(decodedCursor);
			return results.length > 0 ? parsedCursor.fallbackToAllActivity ?? false : false;
		} catch {
			return false;
		}
	};

	return (
		<Fragment>
			<FeedOnboardingMessage
				openActivityFilterModal={openActivityFilterModal}
				servingRecommendations={servingRecommendations}
			/>
			<FeedCardsWrapper
				loading={loading}
				firstPageError={error}
				networkStatus={networkStatus}
				endCursor={endCursor}
				hasNextPage={hasNextPage}
				loadNextPage={loadNextPage}
				openActivityFilterModal={openActivityFilterModal}
				feedType={FeedType.FOLLOWING}
			>
				{shouldFallbackBasedOnCursor(endCursor) && (
					<FallbackToAllActivityBannerWrapper>
						<Inline space="space.100">
							<FeedbackIcon label="" spacing="spacious" />
							<span>
								<Text>{intl.formatMessage(i18n.allActivity)}</Text>
							</span>
							<Button
								onClick={handleManageButtonClick}
								theme={linkButtonTheme}
								appearance="link"
								spacing="none"
							>
								<Text>{intl.formatMessage(i18n.manageFeed)}</Text>
							</Button>
						</Inline>
					</FallbackToAllActivityBannerWrapper>
				)}
				{results.map((result, index) => {
					return (
						<Fragment key={result.content.id}>
							{result && (
								<FeedCard
									feedItem={result}
									ranking={index}
									disableAnimation={disableAnimation}
									shouldHideComments={shouldHideComments}
									followingFeedSortBy={followingFeedSortBy}
								/>
							)}
							<ExitFeedAllyButton
								onExit={handleFocusRef}
								source="following-feed"
								feedCardIndex={index}
							/>
							{index === 2 && showFeedRecs && (
								<>
									<FeedRecommendationsContainer>
										{recsLoading ? (
											<SpinnerWrapper>
												<AkSpinner size="large" />
											</SpinnerWrapper>
										) : (
											<FeedRecommendations
												position={index + 1}
												setShowFeedRecs={setShowFeedRecs}
												recs={recs}
												setRecs={setRecs}
												feedRecError={feedRecError}
											/>
										)}
									</FeedRecommendationsContainer>
									<ExitFeedAllyButton
										onExit={handleFocusRef}
										source="following-feed"
										isAfterRecommendations
									/>
								</>
							)}
						</Fragment>
					);
				})}
			</FeedCardsWrapper>
		</Fragment>
	);
};
